import React, { useRef, useState } from 'react'
import moment from 'moment'
import { DynamicEntityIndex, FlashMessage, GlobalLoader, ModalAlert } from '@reactiveonline/reactive_admin_shared-packages'
import { dynamicTableItemRenderer } from '@reactiveonline/reactive_admin_shared-packages/utils'
import GlobalImports from '../shared/GlobalImports'
import Drawer from '../shared/Drawer'
import PresetDateRangePicker from '../shared/PresetDateRangePicker'

export default function Index({
  appProps, canManagePayouts, commissionLines, entityPath, filters, icon, initialTotalPages, listColumns, selectedColumns, totalCount
}) {
  const flashMessageRef = useRef(null)
  const [dateRange, setDateRange] = useState({
    start_date: moment().subtract(30, 'days'),
    end_date: moment()
  })
  const [renderKey, setRenderKey] = useState(0)
  const [fetchOnStart, setFetchOnStart] = useState(false)
  const [selectedWorkflowAction, setSelectedWorkflowAction] = useState(null)
  const [selectedCommissionLine, setSelectedCommissionLine] = useState(null)
  const [loading, setLoading] = useState(false)

  const itemRenderer = (item, columns) => (
    <>
      { columns.map(column => dynamicTableItemRenderer(item, column)) }
    </>
  )

  function handleApplyDateRange(updatedDateRange) {
    setDateRange(updatedDateRange)
    setFetchOnStart(true)
    setRenderKey(current => current + 1)
  }

  function refreshTable() {
    setFetchOnStart(true)
    setRenderKey(current => current + 1)
  }

  function handleWorkflowAction() {
    if (!selectedWorkflowAction) return

    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: `${ entityPath }/${ selectedWorkflowAction.item.apiUid }/${ selectedWorkflowAction.action }`,
      dataType: 'json',
      success: res => {
        setSelectedWorkflowAction(null)
        setSelectedCommissionLine(null)
        setLoading(false)
        flashMessageRef.current.show(res.message)
        refreshTable()
      },
      error: res => {
        setLoading(false)
        flashMessageRef.current.show(res.message, 'error')
      }
    })
  }

  const actions = [
    {
      icon: 'fa-eye',
      onClick: (event, item) => {
        event.preventDefault()
        setSelectedCommissionLine(item)
      }
    }
  ]

  function availableWorkflowAction(item) {
    if (!canManagePayouts) return null
    if (item?.statusKey === 'pending') return 'mark_payable'
    if (item?.statusKey === 'payable') return 'mark_paid'

    return null
  }

  function workflowActionText(action) {
    if (action === 'mark_payable') return Partners.translations.commissionLine.mark_payable_action
    if (action === 'mark_paid') return Partners.translations.commissionLine.mark_paid_action
    if (action === 'mark_canceled') return Partners.translations.commissionLine.mark_canceled_action

    return null
  }

  function workflowConfirmation() {
    if (selectedWorkflowAction?.action === 'mark_payable') {
      return Partners.translations.commissionLine.mark_payable_confirmation
    }

    if (selectedWorkflowAction?.action === 'mark_canceled') {
      return Partners.translations.commissionLine.mark_canceled_confirmation
    }

    return Partners.translations.commissionLine.mark_paid_confirmation
  }

  function drawerAction() {
    const action = availableWorkflowAction(selectedCommissionLine)
    if (!action) return null

    return () => setSelectedWorkflowAction({ action: action, item: selectedCommissionLine })
  }

  function drawerSecondaryActions() {
    if (!canManagePayouts || !['pending', 'payable'].includes(selectedCommissionLine?.statusKey)) return []

    return [
      {
        text: Partners.translations.commissionLine.mark_canceled_action,
        className: 'reverted',
        disabled: loading,
        action: () => setSelectedWorkflowAction({ action: 'mark_canceled', item: selectedCommissionLine })
      }
    ]
  }

  const detailColumns = listColumns || []

  return (
    <>
      <div className='page-wrapper-header flex-box content-space-between'>
        <div>
          <div className='page-header'>
            <i className={ `fa-light ${ icon }` }/>
            <h1 className='page-header-title'>
              { Partners.translations.commissionLine.title }
            </h1>
          </div>
        </div>

        <div className='flex-box items-center'>
          <PresetDateRangePicker
            value={ dateRange }
            onApply={ handleApplyDateRange }
            maxDateRangeDays={ 365 }
          />
        </div>
      </div>

      <DynamicEntityIndex
        key={ renderKey }
        initialItems={ commissionLines }
        initialTotalPages={ initialTotalPages }
        initialTotalCount={ totalCount }
        fetchOnStart={ fetchOnStart }
        disableDelete
        entity='commission_lines'
        filters={ filters }
        tabs={ [] }
        tableHeadings={ selectedColumns }
        disableCustomization
        itemRenderer={ itemRenderer }
        itemsPath={ entityPath }
        icon={ icon }
        noResourcesTitle={ Partners.translations.commissionLine.no_commission_lines }
        listColumns={ listColumns }
        columnTranslations={ Partners.translations.columns }
        urlParams={ `date_range=${ JSON.stringify(dateRange) }` }
        actions={ actions }
      />

      <Drawer
        visible={ !!selectedCommissionLine }
        closeDrawer={ () => setSelectedCommissionLine(null) }
        title={ Partners.translations.commissionLine.details }
        subtitle={ selectedCommissionLine?.organization }
        action={ drawerAction() }
        actionText={ workflowActionText(availableWorkflowAction(selectedCommissionLine)) }
        disableAction={ loading }
        secondaryActions={ drawerSecondaryActions() }
      >
        { !!selectedCommissionLine &&
          <div className='flex-box flex-column' style={{ gap: 12 }}>
            { detailColumns.map(column => (
              <div
                key={ column.label }
                className='flex-box content-space-between items-start'
                style={{ borderBottom: '1px solid #eef0f4', gap: 20, padding: '0 0 12px' }}
              >
                <div style={{ color: '#697386', fontSize: 13 }}>
                  { Partners.translations.columns[column.label] || column.label }
                </div>
                <div className='bold' style={{ color: '#344054', fontSize: 13, textAlign: 'right' }}>
                  { selectedCommissionLine[column.label] ?? '-' }
                </div>
              </div>
            )) }
          </div>
        }
      </Drawer>

      { !!selectedWorkflowAction &&
        <ModalAlert
          alert={ workflowConfirmation() }
          onSelect={ handleWorkflowAction }
          onClose={ () => setSelectedWorkflowAction(null) }
        />
      }

      <FlashMessage ref={ flashMessageRef } />

      { loading && <GlobalLoader fullPage /> }

      <GlobalImports appProps={ appProps } />
    </>
  )
}
